import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { AlertComponent, ButtonComponent, InputComponent, InputErrorsComponent, MbsPopupType, StepComponent } from 'mbs-ui-kit';
import parseValidationState from 'mbs-ui-shared@components/reserve-auth-add/utils/parseValidationState';
import { ReserveAuthResendCodeButtonComponent } from 'mbs-ui-shared@components/reserve-auth-resend-code-button/reserve-auth-resend-code-button.component';
import { ReserveAuthSettings, ReserveAuthSteps } from 'mbs-ui-shared@models/tfa';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { getSecondsFromDate } from 'mbs-ui-shared@utils';
import { finalize } from 'rxjs';

@Component({
  selector: 'reserve-auth-confirm',
  templateUrl: './reserve-auth-confirm.component.html',
  standalone: true,
  styleUrls: ['./reserve-auth-confirm.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    ReserveAuthResendCodeButtonComponent,
    AlertComponent,
    I18NextPipe,
    InputErrorsComponent,
    StepComponent,
    InputComponent
  ]
})
export class ReserveAuthConfirmComponent {
  private tfaService = inject(TFAService);
  private cdr = inject(ChangeDetectorRef);

  @Input() reserveAuthSettings: ReserveAuthSettings;
  @Input() authId: string;
  @Output() confirmed = new EventEmitter<boolean>();

  public secondsToResend = 0;
  public stepLoading: ReserveAuthSteps = null;
  public step = ReserveAuthSteps.SendPhoneCode;
  public form = new FormGroup({
    phoneCode: new FormControl(null, [Validators.required]),
    emailCode: new FormControl(null, [Validators.required])
  });

  public readonly ReserveAuthSteps = ReserveAuthSteps;
  public readonly MbsPopupType = MbsPopupType;

  public sendSMS() {
    this.stepLoading = ReserveAuthSteps.SendPhoneCode;

    this.tfaService
      .sendSMS({
        authId: this.authId
      })
      .pipe(
        finalize(() => {
          this.stepLoading = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (value) => {
          this.step = ReserveAuthSteps.ConfirmPhoneCode;

          this.setSecondsToResendAvailability(value.retryAvailableDate);

          this.cdr.markForCheck();
        }
      });
  }

  public confirmPhone() {
    this.stepLoading = ReserveAuthSteps.ConfirmPhoneCode;

    this.tfaService
      .confirmSMS({
        authId: this.authId,
        code: this.form.controls.phoneCode.value
      })
      .pipe(
        finalize(() => {
          this.stepLoading = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        error: (error) => {
          this.form.controls.phoneCode.setErrors([{ message: parseValidationState(error.error.errors, 'code') }]);
        },
        next: (value) => {
          if (value.isValid) {
            this.step = ReserveAuthSteps.SendEmailCode;
          } else {
            this.form.controls.phoneCode.setErrors([{ message: 'Verification code entered incorrectly' }]);
          }
        }
      });
  }

  public sendEmail() {
    this.stepLoading = ReserveAuthSteps.SendEmailCode;

    this.tfaService
      .sendEmail({
        authId: this.authId
      })
      .pipe(
        finalize(() => {
          this.stepLoading = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (value) => {
          this.step = ReserveAuthSteps.ConfirmEmailCode;

          this.setSecondsToResendAvailability(value.retryAvailableDate);
          this.cdr.markForCheck();
        }
      });
  }

  public confirmEmail() {
    this.stepLoading = ReserveAuthSteps.ConfirmEmailCode;

    this.tfaService
      .confirmEmail({
        authId: this.authId,
        code: this.form.controls.emailCode.value
      })
      .pipe(
        finalize(() => {
          this.stepLoading = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        error: (error) => {
          this.form.controls.emailCode.setErrors([{ message: parseValidationState(error.error.errors, 'code') }]);
        },
        next: (value) => {
          if (value.isValid) {
            this.step = ReserveAuthSteps.Complete;
            this.confirmed.emit(true);
          } else {
            this.form.controls.emailCode.setErrors([{ message: 'Verification code entered incorrectly' }]);
          }
        }
      });
  }

  private setSecondsToResendAvailability(retryAvailableDate: string) {
    this.secondsToResend = retryAvailableDate ? getSecondsFromDate(retryAvailableDate) : 0;
  }
}
